import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Button,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import GetAppIcon from "@material-ui/icons/GetApp";
import { BlobProvider } from "@react-pdf/renderer";
import { BASE_URL } from "utils/constants";
import ReportPDF from "./ReportPDF";

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    backgroundColor: "#f5f5f5",
    fontFamily: "'Public Sans', sans-serif",
    display: "flex",
    flexDirection: "column",
  },

  // ── Header ──
  header: {
    backgroundColor: "#ffffff",
    borderBottom: "1px solid #e0e0e0",
    padding: "20px 32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 16,
    [theme.breakpoints.down("sm")]: {
      padding: "16px 16px",
    },
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 16,
  },
  companyLogo: {
    height: 44,
    maxWidth: 160,
    objectFit: "contain",
  },
  companyName: {
    fontWeight: 700,
    fontSize: 18,
    color: "#1a1a1a",
    fontFamily: "'Public Sans', sans-serif",
  },
  reportTitle: {
    fontWeight: 600,
    fontSize: 15,
    color: "#444444",
    fontFamily: "'Public Sans', sans-serif",
    marginTop: 2,
  },

  // ── Download button ──
  downloadBtn: {
    backgroundColor: "#1976d2",
    color: "#ffffff",
    textTransform: "none",
    fontFamily: "'Public Sans', sans-serif",
    fontWeight: 600,
    whiteSpace: "nowrap",
    "&:hover": {
      backgroundColor: "#1565c0",
    },
  },

  // ── PDF viewer area ──
  pdfArea: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    padding: "24px 32px",
    [theme.breakpoints.down("sm")]: {
      padding: "16px",
    },
  },
  iframe: {
    flex: 1,
    width: "100%",
    minHeight: "80vh",
    border: "none",
    borderRadius: 6,
    boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
    display: "block",
    backgroundColor: "#ffffff",
  },

  // ── States ──
  centeredState: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 16,
    padding: "40px 24px",
    textAlign: "center",
  },
  stateTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: "#333333",
    fontFamily: "'Public Sans', sans-serif",
    marginBottom: 8,
  },
  stateBody: {
    fontSize: 15,
    color: "#666666",
    maxWidth: 480,
    lineHeight: 1.6,
    fontFamily: "'Public Sans', sans-serif",
  },

  // ── Footer ──
  footer: {
    textAlign: "center",
    padding: "14px",
    borderTop: "1px solid #e0e0e0",
    backgroundColor: "#ffffff",
  },
  footerText: {
    fontSize: 12,
    color: "#aaaaaa",
    fontFamily: "'Public Sans', sans-serif",
  },
}));

// ─── Helpers ──────────────────────────────────────────────────────────────────

function buildFilename(report) {
  const base = report && report.title ? report.title : "report";
  return `${base.replace(/[^a-z0-9]/gi, "_").toLowerCase()}.pdf`;
}

// ─── PDF download + preview panel ────────────────────────────────────────────

function PDFPanel({ report, classes }) {
  const [retryKey, setRetryKey] = useState(0);
  const filename = buildFilename(report);

  const handleRetry = useCallback(() => {
    setRetryKey((k) => k + 1);
  }, []);

  return (
    <BlobProvider key={retryKey} document={<ReportPDF report={report} />}>
      {({ url, loading, error }) => {
        if (loading) {
          return (
            <Box className={classes.centeredState}>
              <CircularProgress size={40} color="primary" />
              <Typography className={classes.stateBody}>
                Generating PDF preview...
              </Typography>
            </Box>
          );
        }

        if (error) {
          return (
            <Box className={classes.centeredState}>
              <Typography className={classes.stateBody} style={{ color: "#c62828" }}>
                Failed to generate the PDF preview.
              </Typography>
              <Button variant="outlined" color="primary" size="small" onClick={handleRetry}>
                Retry
              </Button>
            </Box>
          );
        }

        if (url) {
          return (
            <iframe
              className={classes.iframe}
              src={url}
              title="Report PDF Preview"
            />
          );
        }

        return null;
      }}
    </BlobProvider>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * PublicReportView
 *
 * Standalone page — no login required.
 * Reads `?token=XXX` from the URL and fetches the report data from the
 * public endpoint. Renders a PDF preview using ReportPDF (via BlobProvider)
 * and provides a "Download PDF" button.
 *
 * Auth bypass strategy:
 *   Uses native fetch() (not the axios singleton) to avoid the global
 *   Authorization header set by AuthContext (axios.defaults.headers.common).
 *   This mirrors the same pattern used in PublicSharePage.
 *
 * Routing bypass strategy:
 *   root.router.js intercepts window.location.pathname === "/report-view"
 *   before the auth/loading guard, rendering this component standalone.
 */
function PublicReportView() {
  const classes = useStyles();

  // Read ?token= from query string — React 16 compatible (no useSearchParams)
  const token = new URLSearchParams(window.location.search).get("token");

  // "loading" | "success" | "not_found" | "error"
  const [status, setStatus] = useState("loading");
  const [report, setReport] = useState(null);

  useEffect(() => {
    if (!token) {
      setStatus("not_found");
      return;
    }

    const url = `${BASE_URL}/api/public/reports/${token}/data`;

    fetch(url, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    })
      .then(async (response) => {
        const body = await response.json().catch(() => ({}));

        if (response.status === 404) {
          setStatus("not_found");
          return;
        }

        if (response.ok && body.status === "success" && body.data) {
          setReport(body.data);
          setStatus("success");
          return;
        }

        setStatus("error");
      })
      .catch(() => {
        setStatus("error");
      });
  }, [token]);

  // ── No token ──
  if (!token) {
    return (
      <Box className={classes.root}>
        <Box className={classes.centeredState} style={{ flex: 1 }}>
          <Typography className={classes.stateTitle}>Invalid link</Typography>
          <Typography className={classes.stateBody}>
            This link does not appear to be valid. Please check the URL and try again.
          </Typography>
        </Box>
        <PageFooter classes={classes} />
      </Box>
    );
  }

  // ── Loading ──
  if (status === "loading") {
    return (
      <Box className={classes.root}>
        <Box className={classes.centeredState} style={{ flex: 1 }}>
          <CircularProgress size={44} color="primary" />
        </Box>
        <PageFooter classes={classes} />
      </Box>
    );
  }

  // ── 404 / expired ──
  if (status === "not_found") {
    return (
      <Box className={classes.root}>
        <MinimalHeader classes={classes} />
        <Box className={classes.centeredState} style={{ flex: 1 }}>
          <Typography className={classes.stateTitle}>
            This link is no longer valid or has expired.
          </Typography>
          <Typography className={classes.stateBody}>
            The report you are looking for is not available. Please contact the company that sent you this link.
          </Typography>
        </Box>
        <PageFooter classes={classes} />
      </Box>
    );
  }

  // ── Generic error ──
  if (status === "error") {
    return (
      <Box className={classes.root}>
        <MinimalHeader classes={classes} />
        <Box className={classes.centeredState} style={{ flex: 1 }}>
          <Typography className={classes.stateTitle}>
            Could not load the report.
          </Typography>
          <Typography className={classes.stateBody}>
            Please try again later or contact the company that sent you this link.
          </Typography>
        </Box>
        <PageFooter classes={classes} />
      </Box>
    );
  }

  // ── Success ──
  const companyName =
    report.company?.name || report.company_name || report.companyName || "";
  const companyLogo = report.company?.logo_url || null;
  const safeTitle = report.title || "Report";
  const filename = buildFilename(report);

  return (
    <Box className={classes.root}>
      {/* ── Header ── */}
      <Box className={classes.header}>
        <Box className={classes.headerLeft}>
          {companyLogo && (
            <img
              src={companyLogo}
              alt={companyName}
              className={classes.companyLogo}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          )}
          <Box>
            {companyName ? (
              <Typography className={classes.companyName}>{companyName}</Typography>
            ) : null}
            <Typography className={classes.reportTitle}>{safeTitle}</Typography>
          </Box>
        </Box>

        {/* Download button — uses BlobProvider URL once PDF is ready */}
        <DownloadButton report={report} filename={filename} classes={classes} />
      </Box>

      {/* ── PDF preview ── */}
      <Box className={classes.pdfArea}>
        <PDFPanel report={report} classes={classes} />
      </Box>

      <PageFooter classes={classes} />
    </Box>
  );
}

// ─── Download button (separate BlobProvider instance) ─────────────────────────

function DownloadButton({ report, filename, classes }) {
  return (
    <BlobProvider document={<ReportPDF report={report} />}>
      {({ url, loading }) => (
        <a
          href={url || undefined}
          download={filename}
          style={{ textDecoration: "none" }}
          aria-disabled={loading || !url}
        >
          <Button
            variant="contained"
            className={classes.downloadBtn}
            startIcon={<GetAppIcon />}
            disabled={loading || !url}
          >
            {loading ? "Preparing PDF..." : "Download PDF"}
          </Button>
        </a>
      )}
    </BlobProvider>
  );
}

// ─── Shared sub-components ────────────────────────────────────────────────────

function MinimalHeader({ classes }) {
  return (
    <Box
      style={{
        backgroundColor: "#ffffff",
        borderBottom: "1px solid #e0e0e0",
        padding: "16px 32px",
      }}
    >
      <Typography
        style={{
          fontWeight: 700,
          fontSize: 16,
          color: "#1a1a1a",
          fontFamily: "'Public Sans', sans-serif",
        }}
      >
        X-Scope Report
      </Typography>
    </Box>
  );
}

function PageFooter({ classes }) {
  return (
    <Box className={classes.footer}>
      <Typography className={classes.footerText}>
        Shared via X-Scope · Powered by SyncKonnect
      </Typography>
    </Box>
  );
}

export default PublicReportView;
